import { AspectMaintainer, Input, type SelectOption, Select } from "@clintonelec/react-storybook";
import {
	agcGainOptions, AntiFlickerMode, antiFlickerModeOptions, ExposureMode, exposureModeOptions, type MaxAgc,
	maxAgcOptions, PIrisControl, ShutterSpeed, ShutterSpeed50Hz, shutterSpeedAntiFlickerMode50HzOptions,
	shutterSpeedAntiFlickerMode60HzOptions, shutterSpeedOptions, SlowShutter, slowShutterOptions
} from "Data/Objects/Camera";
import { produce } from "immer";
import type { ImageSettingsState } from "Interfaces";
import { type Dispatch, memo, type SetStateAction } from "react";

type ExposureProps = {
	localSettings: ImageSettingsState;
	renderPopover: (message: string) => React.ReactElement;
	setLocalSettings: Dispatch<SetStateAction<ImageSettingsState>>;
};

function Exposure(props: ExposureProps) {
	const { localSettings, setLocalSettings, renderPopover } = props;
	const {
		exposureMode, agcGain, eShutterSpeed, maxAgc, baseShutterSpeed, maxShutterSpeed, slowShutter, antiFlickerMode,
		pIrisControl
	} = localSettings.exposure;

	const isManualExposure = exposureMode === ExposureMode.MANUAL;

	const baseShutterSpeedOptions = () => {
		const maxShutterSpeedIndex = Object.values(ShutterSpeed).findIndex(value => value === maxShutterSpeed);

		switch (exposureMode) {
			case ExposureMode.AUTO: {
				switch (antiFlickerMode) {
					case AntiFlickerMode.A_50: {
						return shutterSpeedAntiFlickerMode50HzOptions;
					}

					case AntiFlickerMode.A_60: {
						return shutterSpeedAntiFlickerMode60HzOptions;
					}

					default: {
						return shutterSpeedOptions.filter((option, index) => index >= maxShutterSpeedIndex);
					}
				}
			}

			case ExposureMode.AUTO_MOTION_PRIORITY: {
				switch (antiFlickerMode) {
					case AntiFlickerMode.A_50: {
						return [
							{
								label: ShutterSpeed50Hz.S_100,
								value: ShutterSpeed50Hz.S_100
							}
						];
					}

					case AntiFlickerMode.A_60: {
						return [
							{
								label: ShutterSpeed.S_120,
								value: ShutterSpeed.S_120
							}
						];
					}

					default: {
						return shutterSpeedOptions.filter((option, index) => index >= 2);
					}
				}
			}

			default:
				break;
		}
	};

	const maxShutterSpeedOptions = () => {
		switch (exposureMode) {
			case ExposureMode.AUTO: {
				switch (antiFlickerMode) {
					case AntiFlickerMode.A_50: {
						return shutterSpeedAntiFlickerMode50HzOptions;
					}

					case AntiFlickerMode.A_60: {
						return shutterSpeedAntiFlickerMode60HzOptions;
					}

					default: {
						return shutterSpeedOptions;
					}
				}
			}

			case ExposureMode.AUTO_MOTION_PRIORITY: {
				switch (antiFlickerMode) {
					case AntiFlickerMode.A_50: {
						return shutterSpeedAntiFlickerMode50HzOptions;
					}

					default: {
						return shutterSpeedOptions.filter((option, index) => {
							return index <= 2;
						});
					}
				}
			}

			default: {

				break;
			}
		}
	};

	const pIrisControlOptions = () => {
		const options: SelectOption[] = [
			{
				label: "Auto",
				value: PIrisControl.AUTO
			},
			{
				label: "Manual",
				value: PIrisControl.MANUAL
			},
			{
				label: "Best Quality",
				value: PIrisControl.BEST_QUALITY
			},
			{
				label: "Depth Of Field",
				value: PIrisControl.DEPTH_OF_FIELD
			}
		];

		if (exposureMode === ExposureMode.AUTO_MOTION_PRIORITY) {
			return options.slice(0, 2);
		}

		return options;
	};

	const handleExposureModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.exposureMode = value as ExposureMode;

			switch (value) {
				case ExposureMode.AUTO: {
					switch (antiFlickerMode) {
						case AntiFlickerMode.A_50: {
							draft.exposure.baseShutterSpeed = ShutterSpeed50Hz.S_25;
							draft.exposure.maxShutterSpeed = ShutterSpeed50Hz.S_25;

							break;
						}

						case AntiFlickerMode.A_60: {
							draft.exposure.baseShutterSpeed = ShutterSpeed.S_30;
							draft.exposure.maxShutterSpeed = ShutterSpeed.S_30;

							break;
						}

						default: {

							break;
						}
					}

					break;
				}

				case ExposureMode.AUTO_MOTION_PRIORITY: {
					switch (antiFlickerMode) {
						case AntiFlickerMode.A_50: {
							draft.exposure.baseShutterSpeed = ShutterSpeed50Hz.S_100;
							draft.exposure.maxShutterSpeed = ShutterSpeed50Hz.S_25;

							break;
						}

						default: {
							draft.exposure.baseShutterSpeed = ShutterSpeed.S_120;
							draft.exposure.maxShutterSpeed = ShutterSpeed.S_30;

							break;
						}
					}

					draft.exposure.pIrisControl = PIrisControl.AUTO;

					break;
				}

				default: {

					break;
				}
			}
		}));
	};

	const handleAgcGainSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.agcGain = +value;
		}));
	};

	const handleEShutterSpeedUpdate = (value: string) => {
		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.eShutterSpeed = +value;
		}));
	};

	const handleMaxAgcSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.maxAgc = value as MaxAgc;
		}));
	};

	const handleBaseShutterSpeedSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.baseShutterSpeed = value as ShutterSpeed;
		}));
	};

	const handleMaxShutterSpeedSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const currentBaseShutterSpeedIndex = Object.values(ShutterSpeed).findIndex(speed => speed === baseShutterSpeed);
		const selectedMaxShutterSpeedIndex = Object.values(ShutterSpeed).findIndex(speed => speed === value);

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.maxShutterSpeed = value as ShutterSpeed;

			if (currentBaseShutterSpeedIndex < selectedMaxShutterSpeedIndex) {
				draft.exposure.baseShutterSpeed = value as ShutterSpeed;
			}
		}));
	};

	const handleSlowShutterSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.slowShutter = value as SlowShutter;
		}));
	};

	const handleAntiFlickerModeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.antiFlickerMode = value as AntiFlickerMode;

			switch (value) {
				case AntiFlickerMode.A_50: {
					switch (exposureMode) {
						case ExposureMode.AUTO: {
							draft.exposure.baseShutterSpeed = ShutterSpeed50Hz.S_25;
							draft.exposure.maxShutterSpeed = ShutterSpeed50Hz.S_25;

							break;
						}

						default: {
							draft.exposure.baseShutterSpeed = ShutterSpeed50Hz.S_100;
							draft.exposure.maxShutterSpeed = ShutterSpeed50Hz.S_25;

							break;
						}
					}

					break;
				}

				default: {
					switch (exposureMode) {
						case ExposureMode.AUTO: {
							draft.exposure.baseShutterSpeed = ShutterSpeed.S_30;
							draft.exposure.maxShutterSpeed = ShutterSpeed.S_30;

							break;
						}

						default: {
							draft.exposure.baseShutterSpeed = ShutterSpeed.S_120;
							draft.exposure.maxShutterSpeed = ShutterSpeed.S_30;

							break;
						}
					}

					break;
				}
			}
		}));
	};

	const handlePIrisControlSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalSettings(produce(localSettings, draft => {
			draft.exposure.pIrisControl = value as PIrisControl;
		}));
	};

	const renderInfoIcon = () => {
		if (localSettings.enhancement.wideDynamicRange && !isManualExposure) {
			return renderPopover("To use this setting, go to Enhancement tab and switch 'Wide Dynamic Range' to Off");
		}
	};

	return (
		<div className="two-column-card-content">
			<div className="left-content">
				<AspectMaintainer>
					<div className="camera-snapshot"></div>
				</AspectMaintainer>
			</div>
			<div className="right-content">
				<div className="form-row">
					<span>Exposure Mode</span>
					<Select
						allowClear={ false }
						className="form-select"
						onSelect={ handleExposureModeSelect }
						options={ exposureModeOptions }
						value={ exposureMode }
					/>
				</div>
				<div className="form-row">
					<span>AGC Gain</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ !isManualExposure }
						onSelect={ handleAgcGainSelect }
						options={ agcGainOptions }
						value={ agcGain }
					/>
				</div>
				<div className="form-row">
					<span>E-Shutter Speed</span>
					<div className="form-input image-input-span-wrapper">
						<Input
							disabled={ !isManualExposure }
							hideCharMessage={ true }
							max={ 2000 }
							min={ 4 }
							onUpdate={ handleEShutterSpeedUpdate }
							type="number"
							value={ eShutterSpeed }
						/>
						<span>(4 ~ 2000)</span>
					</div>
				</div>
				<div className="form-row">
					<span>Max AGC</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ isManualExposure }
						onSelect={ handleMaxAgcSelect }
						options={ maxAgcOptions }
						value={ maxAgc }
					/>
				</div>
				<div className="form-row">
					<span>Base Shutter Speed</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={
							isManualExposure
							|| (exposureMode === ExposureMode.AUTO && pIrisControl !== PIrisControl.AUTO)
						}
						onSelect={ handleBaseShutterSpeedSelect }
						options={ baseShutterSpeedOptions() }
						value={ baseShutterSpeed }
					/>
				</div>
				<div className="form-row">
					<span>Max Shutter Speed</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={
							isManualExposure
							|| slowShutter !== SlowShutter.OFF
							|| pIrisControl === PIrisControl.BEST_QUALITY
							|| pIrisControl === PIrisControl.DEPTH_OF_FIELD
						}
						onSelect={ handleMaxShutterSpeedSelect }
						options={ maxShutterSpeedOptions() }
						value={ maxShutterSpeed }
					/>
				</div>
				<div className="form-row">
					<span>Slow Shutter</span>
					{ renderInfoIcon() }
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ isManualExposure || localSettings.enhancement.wideDynamicRange }
						onSelect={ handleSlowShutterSelect }
						options={ slowShutterOptions }
						value={ slowShutter }
					/>
				</div>
				<div className="form-row">
					<span>Anti-Flicker Mode</span>
					{ renderInfoIcon() }
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ isManualExposure || localSettings.enhancement.wideDynamicRange }
						onSelect={ handleAntiFlickerModeSelect }
						options={ antiFlickerModeOptions }
						value={ antiFlickerMode }
					/>
				</div>
				<div className="form-row">
					<span>P-Iris Control</span>
					<Select
						allowClear={ false }
						className="form-select"
						disabled={ isManualExposure }
						onSelect={ handlePIrisControlSelect }
						options={ pIrisControlOptions() }
						value={ pIrisControl }
					/>
				</div>
			</div>
		</div>
	);
}

export default memo(Exposure);
